import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPinIcon,
  ImageOffIcon,
  ZoomOutIcon,
  PersonStandingIcon,
  HeartPulseIcon,
  Settings2Icon,
} from 'lucide-react';
import {
  type SensorType,
  getSensorIcon,
  getSensorColor,
} from '../../utils/sensorUtils';
import {
  Point,
  getBoundingBox,
  getPolygonCentroid,
  pointsToSvgString,
  rectToPoints,
} from '../../utils/polygonUtils';
// Define Sensor Types
export type { SensorType };
export type ViewMode = 'energy' | 'comfort'| 'aqi' | 'devices' | 'temperature' | 'airquality';
export interface Sensor {
  id: string;
  type: SensorType;
  label: string;
  alias?: string;
  roomId?: string;
  active: boolean;
  x: number;
  y: number;
  primary_appliance?: string;
}
import { useZonesTelemetry } from '../../hooks/useZonesTelemetry';
import { getComfortState } from '../../utils/comfort';
import { getAQIGrade, overallAqiScore } from '../../utils/aqiUtils';
import { getEnergyGrade } from '../../utils/energyUtils';
import { ConnectedDeviceItem } from './ConnectedDeviceItem';
import { useConnectedDevices } from '../../hooks/useConnectedDevices';
import { useTranslation } from 'react-i18next';


interface Room {
  id: string;
  name: string;
  temp: number;
  humidity: number;
  energy: number;
  aqi: number;
  icon: any;
}
interface FloorPlanHeatMapProps {
  rooms: Room[];
  selectedRoomId: string;
  onSelectRoom: (id: string) => void;
  isZoomed?: boolean;
  onZoomOut?: () => void;
  onSelectSensor?: (sensor: Sensor) => void;
  selectedSensorId?: string | null;
  viewMode?: ViewMode;
  seniorCareEnabled?: boolean;
  onToggleSeniorCare?: () => void;
  absenceAlarmEnabled?: boolean;
  onToggleAbsenceAlarm?: () => void;
  onOpenEditor?: () => void;
  zoneOverrides?: {
    id: string;
    name?: string;
    top: number;
    left: number;
    width: number;
    height: number;
    points?: Point[];
    sensors: Sensor[];
  }[];
  currentAssetId?: string | null;
  floorPlanUrl?: string | null;
}
// Mock room data for different view modes

const DEFAULT_COORDS = {
  top: '40%',
  left: '40%',
  width: '20%',
  height: '20%'
};
const parsePercent = (val: string) => parseFloat(val.replace('%', ''));
export function FloorPlanHeatMap({
  rooms,
  selectedRoomId,
  onSelectRoom,
  isZoomed = false,
  onZoomOut,
  onSelectSensor,
  selectedSensorId,
  viewMode = 'energy',
  seniorCareEnabled = false,
  onToggleSeniorCare,
  absenceAlarmEnabled = false,
  onToggleAbsenceAlarm,
  onOpenEditor,
  zoneOverrides,
  floorPlanUrl,
  currentAssetId
}: FloorPlanHeatMapProps) {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const [imageBounds, setImageBounds] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);

  const updateImageBounds = useCallback(() => {
    if (!imageRef.current || !imageWrapperRef.current) return;
    const img = imageRef.current;
    const wrapperW = imageWrapperRef.current.clientWidth;
    const wrapperH = imageWrapperRef.current.clientHeight;
    const naturalW = img.naturalWidth || 1;
    const naturalH = img.naturalHeight || 1;
    const aspectRatio = naturalW / naturalH;
    const containerAspect = wrapperW / wrapperH;
    let renderedW: number, renderedH: number;
    if (aspectRatio > containerAspect) {
      renderedW = wrapperW;
      renderedH = wrapperW / aspectRatio;
    } else {
      renderedH = wrapperH;
      renderedW = wrapperH * aspectRatio;
    }
    setImageBounds({
      top: (wrapperH - renderedH) / 2,
      left: (wrapperW - renderedW) / 2,
      width: renderedW,
      height: renderedH
    });
  }, []);

  useEffect(() => {
    const observer = new ResizeObserver(() => updateImageBounds());
    if (imageWrapperRef.current) observer.observe(imageWrapperRef.current);
    return () => observer.disconnect();
  }, [updateImageBounds]);

  // Extract real ThingsBoard ASSET UUIDs from zoneOverrides
  const zoneIds = useMemo(() =>
    (zoneOverrides ?? []).map(z => z.id),
  [zoneOverrides]);

  
  // Real-time telemetry subscription
  const { zonesData } = useZonesTelemetry(
    zoneIds,
    ['temperature', 'humidity', 'CO2', 'TVOCs', 'dust', 'calculated_24h_power', 'comfort', 'presence_state', 'light_state']
  );

  const { devices: connectedDevices } = useConnectedDevices(currentAssetId || null);
  // Build polygon maps: points, centroid, and bounding box for each room
  const roomPolygonMap = useMemo(() => {
    const map: Record<
      string,
      {
        points: Point[];
        centroid: Point;
        bbox: { top: number; left: number; width: number; height: number };
        pointsStr: string;
      }
    > = {};

    if (zoneOverrides) {
      zoneOverrides.forEach((z) => {
        const points =
          z.points && z.points.length >= 3
            ? z.points
            : rectToPoints(z.top || 0, z.left || 0, z.width || 20, z.height || 20);
        const centroid = getPolygonCentroid(points);
        const bbox = getBoundingBox(points);
        map[z.id] = {
          points,
          centroid,
          bbox,
          pointsStr: pointsToSvgString(points),
        };
      });
    }
    return map;
  }, [zoneOverrides]);



  const getAqiColor = (score: number) => {
    if (score >= 80)
    return {
      bg: 'bg-emerald-500/30',
      border: 'border-emerald-400/50',
      text: 'text-emerald-200'
    };
    if (score >= 60)
    return {
      bg: 'bg-cyan-500/30',
      border: 'border-cyan-400/50',
      text: 'text-cyan-200'
    };
    if (score >= 40)
    return {
      bg: 'bg-amber-500/30',
      border: 'border-amber-400/50',
      text: 'text-amber-200'
    };
    return {
      bg: 'bg-red-500/30',
      border: 'border-red-400/50',
      text: 'text-red-200'
    };
  };

  const getSvgColors = (bgClass: string, isSelected: boolean) => {
    if (isSelected) {
      return {
        fill: 'transparent',
        stroke: '#818cf8',
        glow: 'drop-shadow(0 0 8px rgba(129, 140, 248, 0.5))',
      };
    }
    if (!bgClass || bgClass.includes('transparent')) {
      return { fill: 'transparent', stroke: 'transparent', glow: 'none' };
    }
    if (bgClass.includes('emerald') || bgClass.includes('green')) {
      return { fill: 'rgba(16, 185, 129, 0.35)', stroke: 'rgba(52, 211, 153, 0.8)', glow: 'none' };
    }
    if (bgClass.includes('cyan')) {
      return { fill: 'rgba(6, 182, 212, 0.35)', stroke: 'rgba(34, 211, 238, 0.8)', glow: 'none' };
    }
    if (bgClass.includes('blue')) {
      return { fill: 'rgba(59, 130, 246, 0.35)', stroke: 'rgba(96, 165, 250, 0.8)', glow: 'none' };
    }
    if (bgClass.includes('yellow')) {
      return { fill: 'rgba(234, 179, 8, 0.35)', stroke: 'rgba(250, 204, 21, 0.8)', glow: 'none' };
    }
    if (bgClass.includes('amber')) {
      return { fill: 'rgba(245, 158, 11, 0.35)', stroke: 'rgba(251, 191, 36, 0.8)', glow: 'none' };
    }
    if (bgClass.includes('orange')) {
      return { fill: 'rgba(249, 115, 22, 0.35)', stroke: 'rgba(251, 146, 60, 0.8)', glow: 'none' };
    }
    if (bgClass.includes('red')) {
      return { fill: 'rgba(239, 68, 68, 0.35)', stroke: 'rgba(248, 113, 113, 0.8)', glow: 'none' };
    }
    if (bgClass.includes('slate') || bgClass.includes('gray')) {
      return { fill: 'rgba(100, 116, 139, 0.35)', stroke: 'rgba(148, 163, 184, 0.8)', glow: 'none' };
    }
    return { fill: 'rgba(99, 102, 241, 0.30)', stroke: 'rgba(129, 140, 248, 0.8)', glow: 'none' };
  };

  const getRoomColors = (room: Room) => {
    const data = zonesData[room.id] || {};
    
    switch (viewMode) {
      case 'comfort':
      case 'temperature':
        const tempC = data['temperature']?.[0]?.value ? Number(data['temperature'][0].value) : room.temp;
        const humC = data['humidity']?.[0]?.value ? Number(data['humidity'][0].value) : 50; // fallback humidity 50
        return getComfortState(tempC, humC);

      case 'aqi':
      case 'airquality':
        const co2 = data['CO2']?.[0]?.value ? Number(data['CO2'][0].value) : 400;
        const tvoc = data['TVOCs']?.[0]?.value ? Number(data['TVOCs'][0].value) : 0.1;
        const pm25 = data['dust']?.[0]?.value ? Number(data['dust'][0].value) : 10;
        const aqiScore = overallAqiScore({ co2, tvoc, pm25 });
        return getAqiColor(aqiScore);

      case 'devices':
        return {
          bg: 'bg-transparent',
          border: 'border-transparent',
          text: 'text-transparent'
        };

      case 'energy':
      default:
        const powerStr = data['calculated_24h_power']?.[0]?.value || 0;
        const energyState = getEnergyGrade(powerStr);
        return {
          bg: energyState.bgColor.replace('/20', '/30'), // adjust opacity for heatmap
          border: `border-${energyState.color.split('-')[1]}-${energyState.color.split('-')[2]}/50`, // extract color e.g. text-emerald-400 -> border-emerald-400/50
          text: energyState.color.replace('400', '200') // make text lighter
        };
    }
  };
  const getRoomDisplayValue = (room: Room) => {
    const data = zonesData[room.id] || {};
    
    switch (viewMode) {
      case 'comfort':
      case 'temperature':
        return '';

      case 'aqi':
      case 'airquality':
        const co2 = data['CO2']?.[0]?.value ? Number(data['CO2'][0].value) : 0;
        const tvoc = data['TVOCs']?.[0]?.value ? Number(data['TVOCs'][0].value) : 0;
        const pm25 = data['dust']?.[0]?.value ? Number(data['dust'][0].value) : 0;
        const aqiScore = overallAqiScore({ co2, tvoc, pm25 });
        return t(getAQIGrade(aqiScore).label);

      case 'devices':
        return '';
      case 'energy':
      default:
        const power = data['calculated_24h_power']?.[0]?.value ? Number(data['calculated_24h_power'][0].value) : 0;
        return `${(Number(power)/1000).toFixed(2)} kWh`;
    }
  };
  const getLegendItems = () => {
    switch (viewMode) {

       case 'comfort':
        return [
        {
          color: 'bg-red-500/60',
          label: t('view_modes.comfort.legend_very_hot', '매우 더움')
        },
        {
          color: 'bg-orange-500/60',
          label: t('view_modes.comfort.legend_hot', '더움/불쾌')
        },
        {
          color: 'bg-emerald-500/60',
          label: t('view_modes.comfort.legend_good', '쾌적')
        },
        {
          color: 'bg-yellow-500/60',
          label: t('view_modes.comfort.legend_cool', '서늘/건조')
        },
        {
          color: 'bg-blue-500/60',
          label: t('view_modes.comfort.legend_cold', '추움/결로')
        }];

      case 'aqi':
      case 'airquality':
        return [
        {
          color: 'bg-emerald-500/60',
          label: t('view_modes.aqi.legend_good', '좋음')
        },
        {
          color: 'bg-cyan-500/60',
          label: t('view_modes.aqi.legend_normal', '보통')
        },
        {
          color: 'bg-amber-500/60',
          label: t('view_modes.aqi.legend_bad', '나쁨')
        },
        {
          color: 'bg-red-500/60',
          label: t('view_modes.aqi.legend_very_bad', '매우 나쁨')
        }];

      case 'energy':
        return [
        {
          color: 'bg-emerald-500/60',
          label: t('view_modes.energy.legend_saving', '절약')
        },
        {
          color: 'bg-cyan-500/60',
          label: t('view_modes.energy.legend_normal', '보통')
        },
        {
          color: 'bg-amber-500/60',
          label: t('view_modes.energy.legend_high', '높음')
        },
        {
          color: 'bg-red-500/60',
          label: t('view_modes.energy.legend_excessive', '과다')
        }];

      default:
        return [];
    }
  };
  const getLegendTitle = () => {
    switch (viewMode) {
      case 'comfort':
        return t('view_modes.comfort.title', '쾌적성');
      case 'aqi':
      case 'airquality':
        return t('view_modes.aqi.title', '공기질');
      case 'energy':
      default:
        return t('view_modes.energy.title', '에너지');

    }
  };
  const getZoomTransform = () => {
    if (!isZoomed || !selectedRoomId)
    return {
      x: 0,
      y: 0,
      scale: 1
    };
    const polyInfo = roomPolygonMap[selectedRoomId];
    let top = polyInfo ? polyInfo.bbox.top : 40;
    let left = polyInfo ? polyInfo.bbox.left : 40;
    let width = polyInfo ? polyInfo.bbox.width : 20;
    let height = polyInfo ? polyInfo.bbox.height : 20;

    if (imageBounds && imageWrapperRef.current) {
      const W = imageWrapperRef.current.clientWidth || 1;
      const H = imageWrapperRef.current.clientHeight || 1;
      left = (imageBounds.left + (left / 100 * imageBounds.width)) / W * 100;
      top = (imageBounds.top + (top / 100 * imageBounds.height)) / H * 100;
      width = (width / 100 * imageBounds.width) / W * 100;
      height = (height / 100 * imageBounds.height) / H * 100;
    }

    const centerX = left + width / 2;
    const centerY = top + height / 2;
    
    const scale = Math.max(1.2, Math.min(75 / width, 75 / height, 3.5));
    
    const x = (50 - centerX) * scale;
    const y = (50 - centerY) * scale;
    return {
      x: `${x}%`,
      y: `${y}%`,
      scale
    };
  };
  const zoomState = getZoomTransform();
  return (
    <div className="relative w-full flex flex-col items-center p-2 overflow-y-auto h-full pt-20 pb-12 pl-0 pr-0 custom-scrollbar">
      {/* Edit Button - Top Left */}
      {!isZoomed && onOpenEditor &&
      <div className="absolute top-4 left-4 z-50">
          <button
          onClick={onOpenEditor}
          className="flex items-center gap-2 backdrop-blur-md rounded-full px-3 py-1.5 border shadow-lg transition-all"
          style={{ background: 'var(--mobile-bg-overlay)', borderColor: 'var(--mobile-border)', color: 'var(--mobile-text-muted)' }}
          >
          
            <Settings2Icon className="w-3.5 h-3.5" />
            <span className="text-[0.9rem] font-medium font-bold uppercase tracking-wider">
              편집
            </span>
          </button>
        </div>
      }

      {/* Senior Care Toggle - Top Right */}
      {!isZoomed &&
      <div className="absolute top-4 right-4 z-50 flex flex-col items-end gap-2">
          
          <div className="flex items-center gap-2 backdrop-blur-md rounded-full px-3 py-1.5 border shadow-lg"
            style={{ background: 'var(--mobile-bg-card)', borderColor: 'var(--mobile-border)' }}>
            <HeartPulseIcon
            className="w-3.5 h-3.5 transition-colors"
            style={{ color: seniorCareEnabled ? '#ef4444' : 'var(--mobile-text-muted)' }} />
          
            <span
            className="text-[0.9rem] font-medium font-bold uppercase tracking-wider transition-colors"
            style={{ color: seniorCareEnabled ? '#ef4444' : 'var(--mobile-text-muted)' }}>
            
              시니어케어
            </span>
            <button
            onClick={onToggleSeniorCare}
            aria-label="시니어케어 토글"
            className="relative w-8 h-4.5 rounded-full transition-colors duration-300 focus:outline-none"
            style={{
              width: '2.1rem',
              height: '1.25rem',
              backgroundColor: seniorCareEnabled ? '#ef4444' : 'var(--mobile-border-strong)'
            }}>
            
              <div
              className={`absolute top-0.5 left-0.5 w-3.5 h-3.5 rounded-full bg-white shadow-sm transition-transform duration-300 ${seniorCareEnabled ? 'translate-x-3.5' : 'translate-x-0'}`}
              style={{
                width: '1rem',
                height: '1rem',
                transform: seniorCareEnabled ?
                'translateX(0.82rem)' :
                'translateX(0)'
              }} />
            
            </button>
          </div>

          {/* Absence Alarm Checkbox - Visible only when Senior Care is ON */}
          <AnimatePresence>
            {seniorCareEnabled && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-center gap-2 backdrop-blur-md rounded-full px-3 py-1.5 border shadow-lg cursor-pointer hover:opacity-90 transition-colors"
                style={{ background: 'var(--mobile-bg-card)', borderColor: 'var(--mobile-border)' }}
                onClick={onToggleAbsenceAlarm}
              >
                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors`} style={{ backgroundColor: absenceAlarmEnabled ? '#ef4444' : 'transparent', borderColor: absenceAlarmEnabled ? '#ef4444' : 'var(--mobile-border-strong)' }}>
                  {absenceAlarmEnabled && (
                    <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <span className="text-[0.75rem] font-medium font-bold tracking-wider transition-colors pr-1" style={{ color: absenceAlarmEnabled ? '#ef4444' : 'var(--mobile-text-muted)' }}>
                  {t('dashboard.absence_alarm', '부재중 알림')}
                </span>
              </motion.div>
            )}
          </AnimatePresence>

        </div>
      }

      {/* Reset Zoom Button & Selected Room Name - only when zoomed */}
      {isZoomed && onZoomOut && (
        <div className="absolute top-4 left-4 z-50 flex items-center gap-2">
          <button
            onClick={onZoomOut}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-900/90 border border-cyan-400/60 text-white backdrop-blur-md hover:bg-slate-800 transition-all text-xs font-semibold shadow-lg shadow-black/50 active:scale-95"
          >
            <ZoomOutIcon className="w-3.5 h-3.5 text-cyan-400" />
            <span>{t('dashboard.all_view', '전체 보기')}</span>
          </button>

          {selectedRoomId && (
            <div className="flex items-center gap-1.5 bg-slate-900/85 backdrop-blur-md px-3 py-1.5 rounded-xl border border-white/20 shadow-lg text-xs font-bold text-white tracking-wide">
              <span className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_#22d3ee]" />
              <span>{rooms.find((r) => r.id === selectedRoomId)?.name || ''}</span>
            </div>
          )}
        </div>
      )}

      {/* Main Floor Plan Container with Zoom & Pan */}
      <motion.div
        className="relative w-full flex items-center justify-center flex-1"
        style={{ minHeight: '380px', maxHeight: '520px' }}
        animate={{
          scale: zoomState.scale,
          x: zoomState.x,
          y: zoomState.y,
        }}
        transition={{
          type: 'spring',
          stiffness: 200,
          damping: 25,
        }}
      >
        <div ref={imageWrapperRef} className="relative w-full h-full">
          {floorPlanUrl ? (
            <img
              ref={imageRef}
              src={floorPlanUrl}
              alt="Floor Plan"
              onLoad={() => {
                setImageLoaded(true);
                updateImageBounds();
              }}
              onError={() => setImageError(true)}
              className={`absolute inset-0 w-full h-full object-contain opacity-80 drop-shadow-2xl transition-opacity duration-500 ${
                imageLoaded ? 'opacity-80' : 'opacity-0'
              }`}
              crossOrigin="anonymous"
            />
          ) : null}

          {!imageLoaded && !imageError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3 text-slate-500">
                <div className="w-8 h-8 border-2 border-cyan-400/50 border-t-cyan-400 rounded-full animate-spin" />
                <span className="text-xs font-mono uppercase tracking-wider">
                  {t('dashboard.loading_floorplan', '평면도 로딩 중...')}
                </span>
              </div>
            </div>
          )}

          {imageError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3 text-slate-500">
                <ImageOffIcon className="w-10 h-10 text-slate-600" />
                <span className="text-xs font-mono uppercase tracking-wider">
                  {t('dashboard.no_floorplan', '평면도가 없습니다.')}
                </span>
              </div>
            </div>
          )}

          {/* Connected Devices overlay - only in devices mode */}
          {imageLoaded && imageBounds && viewMode === 'devices' && (
            <div
              className="absolute pointer-events-none z-30"
              style={{
                top: `${imageBounds.top}px`,
                left: `${imageBounds.left}px`,
                width: `${imageBounds.width}px`,
                height: `${imageBounds.height}px`,
              }}
            >
              {connectedDevices.map((dev, idx) => (
                <ConnectedDeviceItem
                  key={dev.id}
                  label={dev.label}
                  toastName={dev.toastName}
                  top={dev.top}
                  left={dev.left}
                  width={dev.width}
                  height={dev.height}
                  colorScheme={dev.colorScheme}
                  delay={0.2 + idx * 0.1}
                />
              ))}
            </div>
          )}

          {imageLoaded && imageBounds && (
            <div
              className="absolute"
              style={{
                top: `${imageBounds.top}px`,
                left: `${imageBounds.left}px`,
                width: `${imageBounds.width}px`,
                height: `${imageBounds.height}px`,
              }}
            >
              {/* SVG Polygon Overlay Layer */}
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                {rooms
                  .filter((r) => r.id !== 'ALL')
                  .map((room) => {
                    const polyInfo = roomPolygonMap[room.id];
                    if (!polyInfo) return null;

                    const isSelected = Boolean(isZoomed && selectedRoomId === room.id);
                    const colors = getRoomColors(room);
                    const svgColors = getSvgColors(colors.bg, isSelected);
                    const isDimmed = isZoomed && !isSelected;

                    return (
                      <polygon
                        key={`poly-${room.id}-${viewMode}`}
                        points={polyInfo.pointsStr}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectRoom(room.id);
                        }}
                        fill={svgColors.fill}
                        stroke={svgColors.stroke}
                        strokeWidth={isSelected ? '0.75' : '0.35'}
                        className="pointer-events-auto cursor-pointer transition-all duration-300 hover:brightness-125"
                        style={{
                          opacity: isDimmed ? 0.2 : 1,
                          filter: svgColors.glow,
                        }}
                      />
                    );
                  })}
              </svg>

              {/* Room Badges & Centroid Indicators */}
              {rooms
                .filter((r) => r.id !== 'ALL')
                .map((room) => {
                  const polyInfo = roomPolygonMap[room.id];
                  if (!polyInfo) return null;

                  const isSelected = Boolean(isZoomed && selectedRoomId === room.id);
                  const colors = getRoomColors(room);
                  const isDimmed = isZoomed && !isSelected;

                  return (
                    <div
                      key={`room-overlay-${room.id}-${viewMode}`}
                      className="absolute pointer-events-auto"
                      style={{
                        left: `${polyInfo.centroid.x}%`,
                        top: `${polyInfo.centroid.y}%`,
                        transform: 'translate(-50%, -50%)',
                        zIndex: isSelected ? 25 : 10,
                        opacity: isDimmed ? 0.3 : 1,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectRoom(room.id);
                      }}
                    >
                      {(!isZoomed || !isSelected) && (
                        <motion.div
                          whileHover={{ scale: 1.08 }}
                          className="relative flex flex-col items-center justify-center cursor-pointer text-center select-none"
                        >
                          <div className="flex items-center gap-1 drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]">
                            <span
                              className={`text-[0.75rem] font-roboto font-bold tracking-wider leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] ${
                                isSelected ? 'text-cyan-300' : 'text-white'
                              }`}
                            >
                              {room.name}
                            </span>
                            <div
                              className={`w-1.5 h-1.5 rounded-full ${
                                isSelected
                                  ? 'bg-cyan-400 shadow-[0_0_6px_#22d3ee]'
                                  : colors.text.replace('text-', 'bg-')
                              }`}
                            />
                          </div>

                          {getRoomDisplayValue(room) ? (
                            <span className="text-[0.7rem] font-roboto font-medium text-slate-100 mt-0.5 leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]">
                              {getRoomDisplayValue(room) as React.ReactNode}
                            </span>
                          ) : null}

                          {/* Senior Care Presence Icon */}
                          {seniorCareEnabled &&
                            Number(zonesData[room.id]?.['presence_state']?.[0]?.value) > 0 && (
                              <div className="absolute -top-3 -right-3 flex items-center justify-center pointer-events-none">
                                <span className="absolute w-5 h-5 rounded-full bg-red-500/30 animate-ping" />
                                <div className="w-4 h-4 rounded-full bg-red-500/80 border border-red-300 flex items-center justify-center shadow-lg">
                                  <PersonStandingIcon className="w-3 h-3 text-white" />
                                </div>
                              </div>
                            )}
                        </motion.div>
                      )}

                    </div>
                  );
                })}

              {/* Zoomed Mode: Sensors inside selected room */}
              {isZoomed &&
                rooms
                  .filter((r) => r.id !== 'ALL' && selectedRoomId === r.id)
                  .map((room) => {
                    const polyInfo = roomPolygonMap[room.id];
                    if (!polyInfo) return null;

                    const zoneSensors = zoneOverrides?.find((z) => z.id === room.id)?.sensors || [];
                    if (zoneSensors.length === 0) return null;

                    return (
                      <div
                        key={`room-sensors-${room.id}`}
                        className="absolute pointer-events-none z-30"
                        style={{
                          top: `${polyInfo.bbox.top}%`,
                          left: `${polyInfo.bbox.left}%`,
                          width: `${polyInfo.bbox.width}%`,
                          height: `${polyInfo.bbox.height}%`,
                        }}
                      >
                        {zoneSensors.map((sensor) => {
                          const Icon = getSensorIcon(sensor.type);
                          const colorClasses = getSensorColor(sensor.type);
                          const isSensorSelected = selectedSensorId === sensor.id;
                          const scaleFactor = isZoomed && zoomState.scale ? 1 / zoomState.scale : 1;

                          return (
                            <div
                              key={sensor.id}
                              className="absolute pointer-events-auto"
                              style={{
                                left: `${sensor.x}%`,
                                top: `${sensor.y}%`,
                                transform: 'translate(-50%, -50%)',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                onSelectSensor?.(sensor);
                              }}
                            >
                              <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{
                                  scale: isSensorSelected ? scaleFactor * 1.25 : scaleFactor,
                                  opacity: 1,
                                }}
                                whileHover={{ scale: scaleFactor * 1.15 }}
                                transition={{ delay: 0.1, type: 'spring', stiffness: 350, damping: 25 }}
                                className="relative group cursor-pointer"
                              >
                                <div
                                  className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center border-2 shadow-lg transition-all duration-300 ${colorClasses} ${
                                    isSensorSelected
                                      ? 'ring-2 ring-white shadow-[0_0_15px_rgba(255,255,255,0.5)]'
                                      : ''
                                  }`}
                                >
                                  <Icon className="w-3 h-3" />
                                </div>
                                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-1.5 py-0.5 bg-black/80 backdrop-blur-sm rounded flex flex-col items-center opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-md pointer-events-none z-50">
                                  <span className="text-[10px] font-bold text-white leading-tight">
                                    {sensor.primary_appliance || sensor.label}
                                  </span>
                                  {sensor.primary_appliance && (
                                    <span className="text-[8px] font-roboto text-slate-300 mt-0.5 leading-tight">
                                      {sensor.label}
                                    </span>
                                  )}
                                </div>
                              </motion.div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
            </div>
          )}
        </div>
      </motion.div>

      {/* Legend - now in flow after map */}
      {imageLoaded && !isZoomed && viewMode !== 'devices' &&
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-40 backdrop-blur-lg px-4 py-3 rounded-2xl border flex flex-wrap items-center justify-center gap-x-4 gap-y-2 shadow-xl mt-12 mb-6 mx-4 w-[calc(100%-32px)]"
        style={{ background: 'var(--mobile-bg-card)', borderColor: 'var(--mobile-border)', color: 'var(--mobile-text-secondary)' }}>
          <MapPinIcon className="w-3.5 h-3.5 text-cyan-500 flex-shrink-0" />
          <span className="text-[0.75rem] font-mono uppercase tracking-wider font-bold" style={{ color: 'var(--mobile-text-primary)' }}>
            {getLegendTitle()}
          </span>
          <span style={{ color: 'var(--mobile-border-strong)' }}>|</span>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            {getLegendItems().map((item) =>
          <span
            key={item.label}
            className="text-[0.7rem] font-mono font-bold flex items-center gap-1.5 whitespace-nowrap"
            style={{ color: 'var(--mobile-text-secondary)' }}>
            
                <span className={`w-2 h-2 rounded-full ${item.color} shadow-sm shadow-black/20`} />
                {item.label}
              </span>
          )}
          </div>
        </motion.div>
      }
    </div>);

}


